import {
  AlertIcon,
  BookIcon,
  CodeIcon,
  DownloadIcon,
  FileDirectoryFillIcon,
  FileIcon,
  FileSubmoduleIcon,
  FileSymlinkFileIcon,
  GitCommitIcon,
  HistoryIcon,
  PlugIcon,
  RepoIcon,
  SearchIcon,
  SyncIcon,
  TerminalIcon,
  TriangleDownIcon,
} from "@primer/octicons-react";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { repoPath, rp } from "../api/client";
import { useAction, useFile, useLog, useRemote, useRepo, useTree, useTreeAll, useWhoami } from "../api/hooks";
import { canIn, type FileView, type LogEntry, type Remote, type RepoRow, type TreeEntry } from "../api/types";
import { CodeView } from "../components/CodeView";
import { Avatar, CopyButton, CopyField, Empty, ErrorMessage, Loading, RefPill, Sha, TimeAgo } from "../components/common";
import { EvidenceIcon } from "../components/StateLabel";
import { forge } from "../components/forge";
import { bytes, cloneHost, short } from "../components/format";
import { Markdown } from "../components/Markdown";
import { Subhead } from "../components/SettingsLayout";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { UnderlineNav, UnderlineNavItem } from "../components/ui/tabs";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Table, TableBody, TableCell, TableRow } from "../components/ui/table";
import { Tooltip } from "../components/ui/tooltip";

/** /:repo and /:repo/src/:ref/* — GitHub's repo home: the file table under the latest commit and the README;
 * a file opens below the same path. (The file-tree layout belongs to change review.) */
export function RepoCode() {
  const { owner = "", repo = "", ref = "main", "*": splat = "" } = useParams();
  const path = splat.replace(/\/+$/, "");
  const [params, setParams] = useSearchParams();
  const repoQ = useRepo(repo);
  const remote = useRemote(repo);
  const tree = useTree(repo, ref, path);
  const isDir = !tree.error;
  const file = useFile(repo, ref, path, !!tree.error && !!path);
  const log = useLog(repo, { limit: 100, path: path || undefined });

  // Before main exists there is no tree: the import status keeps the usual readable column.
  if (repoQ.data && !repoQ.data.info.main) {
    return (
      <div>
        {repoQ.data.repo.import_state ? (
          <ImportStatus row={repoQ.data.repo} />
        ) : remote.data?.remote ? (
          <RemoteImportStatus repo={repo} rem={remote.data.remote} />
        ) : remote.isLoading ? (
          <Loading />
        ) : (
          <Card>
            <Empty icon={RepoIcon} bee="idle">
              This repository is empty. Import history with <code>bee import &lt;git url or dir&gt; --repo {repo}</code>.
            </Empty>
          </Card>
        )}
      </div>
    );
  }

  const crumbs = path ? path.split("/") : [];
  const src = (p: string) => `${rp(repo)}/src/${ref}${p ? `/${p}` : ""}`;
  const f = file.data;
  const latest = log.data?.entries[0];
  return (
    <div className="grid items-start gap-6">
      <div className="min-w-0">
        {/* Titled like every other repo page; the Code menu and "Go to file" sit where its actions go. */}
        <Subhead
          title={repo}
          actions={
            <>
              {isDir && <GoToFile repo={repo} gitRef={ref} />}
              <CodeButton repo={owner ? `${owner}/${repo}` : repo} />
            </>
          }
        >
          {repoQ.data?.repo.description || undefined}
        </Subhead>
        {(path || ref !== "main") && (
          <div className="flex flex-wrap items-center gap-2">
            {/* There is only main; name the ref only when browsing an older commit. */}
            {ref !== "main" && (
              <RefPill>
                <GitCommitIcon size={12} /> {short(ref)}
              </RefPill>
            )}
            {path && (
              <nav aria-label="Path" className="flex min-w-0 flex-wrap items-center gap-1 text-base">
                <Link to={src("")} className="font-semibold">
                  {repo}
                </Link>
                {crumbs.map((c, i) => (
                  <span key={i} className="flex min-w-0 items-center gap-1">
                    <span className="text-muted-foreground">/</span>
                    {i === crumbs.length - 1 ? <b className="truncate">{c}</b> : <Link to={src(crumbs.slice(0, i + 1).join("/"))}>{c}</Link>}
                  </span>
                ))}
                <CopyButton value={path} title="Copy path" />
              </nav>
            )}
          </div>
        )}

        {tree.isLoading || file.isLoading ? (
          <Card>
            <Loading rows={10} />
          </Card>
        ) : isDir && tree.data ? (
          <Folder repo={repo} gitRef={ref} path={path} entries={tree.data.entries} log={log.data?.entries} latest={latest} />
        ) : f ? (
          <>
            {latest && <LatestCommit repo={repo} e={latest} path={path} boxed />}
            <FileBox repo={repo} f={f} plain={params.get("plain") === "1"} setPlain={(on) => setParams(on ? { plain: "1" } : {}, { replace: true })} />
          </>
        ) : (
          <ErrorMessage error={file.error ?? tree.error} />
        )}
      </div>
    </div>
  );
}

/** GitHub's "Go to file": a box over every path in the commit, fetched once someone starts typing. */
function GoToFile({ repo, gitRef }: { repo: string; gitRef: string }) {
  const [text, setText] = useState("");
  const t = text.trim().toLowerCase();
  const all = useTreeAll(repo, gitRef, !!t);
  const nav = useNavigate();
  const hits = (all.data?.entries ?? [])
    .filter((e) => e.type !== "tree" && e.path.toLowerCase().includes(t))
    .sort((a, b) => Number(b.name.toLowerCase().includes(t)) - Number(a.name.toLowerCase().includes(t)) || a.path.length - b.path.length)
    .slice(0, 12);
  const go = (p: string) => {
    setText("");
    nav(`${rp(repo)}/src/${gitRef}/${p}`);
  };
  return (
    <div className="relative max-md:hidden">
      <label className="flex h-8 w-64 items-center gap-2 rounded-md border bg-background px-2 text-sm focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-ring">
        <SearchIcon size={16} className="shrink-0 text-muted-foreground" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setText("");
            if (e.key === "Enter" && hits[0]) go(hits[0].path);
          }}
          placeholder="Go to file"
          aria-label="Go to file"
          className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </label>
      {t && (
        <div className="absolute right-0 top-9 z-20 w-96 overflow-hidden rounded-md border bg-popover py-1 text-sm text-popover-foreground shadow-lg">
          {all.isLoading ? (
            <p className="px-3 py-1.5 text-xs text-faint">Loading files…</p>
          ) : hits.length === 0 ? (
            <p className="px-3 py-1.5 text-xs text-faint">No files match</p>
          ) : (
            hits.map((e) => (
              <button key={e.path} type="button" onClick={() => go(e.path)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-neutral-muted" title={e.path}>
                <FileIcon size={16} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 truncate">
                  {e.name} <span className="text-xs text-muted-foreground">{e.path.slice(0, -e.name.length - 1)}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Who last touched this path, with what and when, and a way into its history: the head of the file table, or its own box above a file. */
function LatestCommit({ repo, e, path, boxed }: { repo: string; e: LogEntry; path: string; boxed?: boolean }) {
  const who = e.agent ?? e.actor;
  const text = e.subject ?? (e.kind === "ref_set" ? "history import" : e.kind);
  return (
    <div className={cn("flex h-12 items-center gap-2 bg-muted px-4 text-sm", boxed ? "mt-4 rounded-md border" : "border-b")}>
      <Avatar name={who} size={20} />
      <b className="shrink-0">{who}</b>
      <Link to={e.change_id ? `${rp(repo)}/changes/${e.change_id}` : `${rp(repo)}/commit/${e.new_sha}`} className="min-w-0 truncate text-muted-foreground hover:text-info">
        {text}
      </Link>
      {e.evidence.pass ? <EvidenceIcon status="pass" /> : e.evidence.fail ? <EvidenceIcon status="fail" /> : null}
      <span className="ml-auto flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground max-md:hidden">
        <Sha repo={repo} sha={e.new_sha} n={7} /> · <TimeAgo ms={e.ts} />
      </span>
      <Button asChild size="sm" variant="ghost" className="shrink-0">
        <Link to={`${rp(repo)}/commits${path ? `?path=${encodeURIComponent(path)}` : ""}`}>
          <HistoryIcon /> History
        </Link>
      </Button>
    </div>
  );
}

/** GitHub's file box: Preview / Code for Markdown, size, then Raw, copy and download; the file below. */
function FileBox({ repo, f, plain, setPlain }: { repo: string; f: FileView; plain: boolean; setPlain: (on: boolean) => void }) {
  const md = f.path.endsWith(".md") && !f.binary;
  const raw = `/v1/repos/${repo}/file?commit=${f.commit}&path=${encodeURIComponent(f.path)}&raw=1`;
  const lines = f.content ? f.content.split("\n").length - (f.content.endsWith("\n") ? 1 : 0) : 0;
  const seg = "h-7 rounded-md px-3 text-sm font-semibold text-muted-foreground aria-pressed:bg-background aria-pressed:text-foreground aria-pressed:shadow-sm";
  return (
    <Card>
      <div className="flex h-12 items-center gap-2 border-b bg-muted px-2">
        <div className="inline-flex rounded-md border bg-neutral-muted p-0.5">
          {md && (
            <button type="button" className={seg} aria-pressed={!plain} onClick={() => setPlain(false)}>
              Preview
            </button>
          )}
          <button type="button" className={seg} aria-pressed={!md || plain} onClick={() => setPlain(true)}>
            Code
          </button>
        </div>
        <span className="truncate text-xs text-muted-foreground">
          {f.content != null && !f.binary ? `${lines} ${lines === 1 ? "line" : "lines"} · ` : ""}
          {bytes(f.size)}
        </span>
        <span className="ml-auto flex items-center gap-1">
          <Button asChild size="sm">
            <a href={raw}>Raw</a>
          </Button>
          {f.content && <CopyButton value={f.content} title="Copy raw file" />}
          <Tooltip content="Download raw file">
            <Button asChild variant="ghost" size="icon-sm">
              <a href={raw} download={f.path.split("/").pop()} aria-label="Download raw file">
                <DownloadIcon />
              </a>
            </Button>
          </Tooltip>
        </span>
      </div>
      {f.binary ? (
        <Empty>Binary file ({bytes(f.size)})</Empty>
      ) : md && !plain ? (
        <div className="mx-auto max-w-[1012px] px-8 py-6">
          <Markdown source={f.content ?? ""} />
        </div>
      ) : (
        <CodeView path={f.path} content={f.content ?? ""} />
      )}
    </Card>
  );
}

/** A folder, as GitHub's file table: the latest commit as its head, each entry with the change that last touched it, then the README. */
function Folder({ repo, gitRef, path, entries, log, latest }: { repo: string; gitRef: string; path: string; entries: TreeEntry[]; log?: LogEntry[]; latest?: LogEntry }) {
  const up = path.split("/").slice(0, -1).join("/");
  return (
    <>
      <Card>
        {latest && <LatestCommit repo={repo} e={latest} path={path} />}
        {entries.length === 0 ? (
          <Empty icon={FileDirectoryFillIcon}>{path ? "Empty folder" : "No files on this branch"}</Empty>
        ) : (
          <Table className="[&_tr:first-child_td]:border-t-0">
            <TableBody>
              {path && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Link to={`${rp(repo)}/src/${gitRef}${up ? `/${up}` : ""}`} className="inline-flex items-center gap-2 text-foreground hover:text-info" title="Parent folder">
                      <FileDirectoryFillIcon className="text-directory" /> ..
                    </Link>
                  </TableCell>
                </TableRow>
              )}
              {entries.map((e) => {
                const last = lastTouch(log, e);
                return (
                  <TableRow key={e.path}>
                    <TableCell className="w-[38%] max-w-0">
                      <Link className="flex min-w-0 items-center gap-2 text-foreground hover:text-info" to={`${rp(repo)}/src/${gitRef}/${e.path}`}>
                        <EntryIcon type={e.type} />
                        <span className="truncate">{e.name}</span>
                        {e.type === "commit" && <span className="font-mono text-xs text-muted-foreground">@ {short(e.sha, 7)}</span>}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-0 truncate text-muted-foreground max-md:hidden">
                      <LastChange repo={repo} e={last} />
                    </TableCell>
                    <TableCell className="w-28 whitespace-nowrap text-right text-muted-foreground max-md:hidden">{last && <TimeAgo ms={last.ts} />}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
      <Readme repo={repo} commit={gitRef} entries={entries.map((e) => e.path)} />
    </>
  );
}

/** An empty repo created from a GitHub URL: bee is cloning it, or it failed. */
function ImportStatus({ row }: { row: RepoRow }) {
  const me = useWhoami();
  const retry = useAction(row.name);
  const from = (
    <>
      GitHub <code>{row.import_url}</code>
      {row.import_branch && (
        <>
          {" "}
          (branch <code>{row.import_branch}</code>)
        </>
      )}
    </>
  );
  const admin = canIn(me.data, "admin", row.name);
  if (row.import_state !== "failed") {
    return (
      <Card>
        <Empty icon={SyncIcon} spin>
          Importing from {from}…
          <div className="mt-2 text-xs text-muted-foreground">
            <code>bee import --repo {row.name}</code> clones the repository and uploads its history. This page updates when main is set.
          </div>
        </Empty>
        <ImportHelp row={row} admin={admin} />
      </Card>
    );
  }
  return (
    <Card>
      <CardContent>
        <Alert variant="error" className="mt-0">
          <AlertTitle>
            <AlertIcon /> Import from {from} failed
          </AlertTitle>
          {row.import_error && <pre className="mt-2 whitespace-pre-wrap font-mono text-xs">{row.import_error}</pre>}
        </Alert>
        {admin && (
          <div className="mt-3 flex items-center gap-2">
            <Button disabled={retry.isPending} onClick={() => retry.mutate({ path: repoPath(row.name, "/admin/import/retry"), body: {} })}>
              <SyncIcon /> {retry.isPending ? "Retrying…" : "Retry import"}
            </Button>
          </div>
        )}
        {retry.error ? <ErrorMessage error={retry.error} /> : null}
      </CardContent>
      <ImportHelp row={row} admin={admin} />
    </Card>
  );
}

/** An empty repo imported with a remote in pr mode: main is set by the first pull. */
function RemoteImportStatus({ repo, rem }: { repo: string; rem: Remote }) {
  const f = forge(rem.provider);
  const from = (
    <>
      {f.name} <code>{rem.url}</code> (branch <code>{rem.branch}</code>)
    </>
  );
  if (rem.error) {
    return (
      <Card>
        <CardContent>
          <Alert variant="error" className="mt-0">
            <AlertTitle>
              <AlertIcon /> Import from {from} failed
            </AlertTitle>
            <pre className="mt-2 whitespace-pre-wrap font-mono text-xs">{rem.error}</pre>
          </Alert>
          <p className="mt-3 text-xs text-muted-foreground">
            bee retries on the next sync. See <Link to={`${rp(repo)}/remote`}>Remote</Link> for the status, or pull now from there.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <Empty icon={SyncIcon} spin>
        Importing from {from}…
        <div className="mt-2 text-xs text-muted-foreground">
          {rem.checked_at == null ? "Fetching the repository for the first time." : "Waiting for the next sync."} This page updates when main is set;{" "}
          <Link to={`${rp(repo)}/remote`}>Remote</Link> shows the progress.
        </div>
      </Empty>
    </Card>
  );
}

/** How to give bee access to a private source, or skip it and import from your own machine. */
function ImportHelp({ row, admin }: { row: RepoRow; admin: boolean }) {
  const url = row.import_url ?? "";
  const https = url.startsWith("https://");
  const host = cloneHost(url);
  const env = "BEE_GITHUB_TOKEN";
  return (
    <CardContent className="border-t text-xs text-muted-foreground">
      <p>
        {https ? (
          <>
            Private repository? <Link to="/profile?tab=account">Connect your {host ?? "host"} account</Link> (or set <code>{env}</code> on the server), then
            retry.
          </>
        ) : (
          <>
            Over SSH bee clones with its own SSH key, which needs access to {host ? <code>{host}</code> : "the host"}. For an HTTPS URL, connect your account
            instead.
          </>
        )}
      </p>
      <p className="mt-2">
        If bee cannot reach the source (an internal host, or only your SSH key has access), import it from your machine. This uses your git credentials and
        finishes this import:
      </p>
      <CopyField value={`bee import --repo ${row.name}`} title="Copy command" className="mt-2" />
    </CardContent>
  );
}

/** GitHub's file-list icons: blue folders, muted gray everything else. */
function EntryIcon({ type }: { type: TreeEntry["type"] }) {
  if (type === "tree") return <FileDirectoryFillIcon className="shrink-0 text-directory" />;
  const Icon = type === "link" ? FileSymlinkFileIcon : type === "commit" ? FileSubmoduleIcon : FileIcon;
  return <Icon className="shrink-0 text-muted-foreground" />;
}

/** The newest log entry that touched this entry (or anything under it), from the directory's recent log. */
function lastTouch(entries: LogEntry[] | undefined, e: TreeEntry) {
  return entries?.find((l) => l.paths?.some((p) => p === e.path || p.startsWith(`${e.path}/`)));
}

/** GitHub's per-row "last commit message" cell. */
function LastChange({ repo, e }: { repo: string; e?: LogEntry }) {
  if (!e) return null;
  const text = e.subject ?? e.kind;
  return e.change_id ? (
    <Link to={`${rp(repo)}/changes/${e.change_id}`} className="text-muted-foreground hover:text-info hover:underline" title={text}>
      {text}
    </Link>
  ) : (
    <span title={text}>{text}</span>
  );
}

function Readme({ repo, commit, entries }: { repo: string; commit: string; entries: string[] }) {
  const readme = entries.find((p) => /(^|\/)readme(\.md)?$/i.test(p));
  const f = useFile(repo, commit, readme ?? "", !!readme);
  if (!readme || !f.data?.content) return null;
  return (
    <Card id="readme">
      <div className="border-b px-2">
        <UnderlineNav aria-label="Repository files">
          <UnderlineNavItem current>
            <Link to={`${rp(repo)}/src/${commit}/${readme}`}>
              <BookIcon size={16} /> {readme.split("/").pop()?.replace(/\.md$/i, "").toUpperCase()}
            </Link>
          </UnderlineNavItem>
        </UnderlineNav>
      </div>
      <div className="px-8 py-6">
        <Markdown source={f.data.content} />
      </div>
    </Card>
  );
}

function MenuSection({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="p-4 [&+&]:border-t [&+&]:border-border-muted">
      <div className="mb-2 flex items-center gap-2 font-semibold [&_svg]:text-muted-foreground">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

/** GitHub's green "Code" menu: how to get this repository locally. */
function CodeButton({ repo }: { repo: string }) {
  const open = `bee open ${repo}`;
  const map = `bee map --repo ${repo}`;
  // The CLI defaults to production, so only name the server when this page is served from elsewhere.
  const env: Record<string, string> = { BEE_TOKEN: "<agent token>" };
  if (window.location.origin !== "https://snapaction.io") env.BEE_URL = window.location.origin;
  const mcp = JSON.stringify({ mcpServers: { bee: { command: "bee", args: ["mcp", "--root", "/work"], env } } }, null, 2);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="primary">
          <CodeIcon /> Code <TriangleDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <MenuSection icon={<TerminalIcon />} title="Agents">
          <CopyField value={open} title="Copy command" />
          <p className="mt-2 text-xs text-muted-foreground">
            Opens a snapshot of main to edit and land with <code>bee land</code>.
          </p>
          <CopyField value={map} title="Copy command" className="mt-3" />
          <p className="mt-2 text-xs text-muted-foreground">Ranked definitions with line ranges across the whole repo, without a checkout.</p>
        </MenuSection>
        <MenuSection icon={<PlugIcon />} title="MCP">
          <div className="relative rounded-md border bg-muted">
            <pre className="overflow-x-auto p-2 pr-9 font-mono text-xs">{mcp}</pre>
            <span className="absolute right-1 top-1">
              <CopyButton value={mcp} title="Copy config" />
            </span>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            For harnesses without a shell. Cursor, Claude Code and Codex can use <code>bee install</code> instead.
          </p>
        </MenuSection>
      </PopoverContent>
    </Popover>
  );
}
